import { supabase } from '../lib/supabase';

export interface PrivacySettings {
  profile_visibility: 'public' | 'private' | 'friends_only';
  discoverable_by_email: boolean;
  discoverable_by_phone: boolean;
  discoverable_by_username: boolean;
  allow_friend_requests: boolean;
  show_online_status: boolean;
  show_activity_status: boolean;
  who_can_message: 'everyone' | 'friends' | 'nobody';
  who_can_see_images: 'everyone' | 'friends' | 'nobody';
  who_can_see_favorites: 'everyone' | 'friends' | 'nobody';
  who_can_see_stats: 'everyone' | 'friends' | 'nobody';
}

export interface UserSearchResult {
  user_id: string;
  username: string;
  display_name: string;
  email?: string;
  avatar_url?: string;
  is_verified: boolean;
  connection_status: 'none' | 'pending' | 'accepted' | 'declined' | 'blocked';
  mutual_connections_count: number;
}

export interface UserConnection {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: 'pending' | 'accepted' | 'declined' | 'blocked';
  created_at: string;
  updated_at: string;
  requester?: {
    username: string;
    display_name: string;
    avatar_url: string | null;
  };
  addressee?: {
    username: string;
    display_name: string;
    avatar_url: string | null;
  };
}

export interface UserProfile {
  id: string;
  username: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
  phone_number: string | null;
  is_phone_verified: boolean;
  privacy_settings: PrivacySettings;
  last_active_at: string;
  is_online: boolean;
  created_at: string;
  updated_at: string;
}

class PrivacyService {
  // Get current user's privacy settings
  async getPrivacySettings(): Promise<PrivacySettings | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('profiles')
        .select('privacy_settings')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data?.privacy_settings || null;
    } catch (error) {
      console.error('Error getting privacy settings:', error);
      return null;
    }
  }

  // Update privacy settings
  async updatePrivacySettings(settings: Partial<PrivacySettings>): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.rpc('update_privacy_settings', {
        user_id_param: user.id,
        new_settings: settings
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating privacy settings:', error);
      return false;
    }
  }

  // Update phone number
  async updatePhoneNumber(phoneNumber: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({ 
          phone_number: phoneNumber,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating phone number:', error);
      return false;
    }
  }

  // Search users
  async searchUsers(
    query: string, 
    searchType: 'all' | 'username' | 'email' | 'phone' | 'display_name' = 'all',
    limit: number = 20
  ): Promise<UserSearchResult[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('search_users', {
        searcher_id_param: user.id,
        search_query_param: query,
        search_type_param: searchType,
        limit_param: limit
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }

  // Send friend request
  async sendFriendRequest(userId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('send_friend_request', {
        requester_id_param: user.id,
        addressee_id_param: userId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending friend request:', error);
      return { success: false, error: 'Failed to send friend request' };
    }
  }

  // Respond to friend request
  async respondToFriendRequest(
    requesterId: string, 
    response: 'accept' | 'decline'
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('respond_to_friend_request', {
        user_id_param: user.id,
        requester_id_param: requesterId,
        response_param: response
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error responding to friend request:', error);
      return { success: false, error: 'Failed to respond to friend request' };
    }
  }

  // Block/unblock user
  async toggleUserBlock(
    userId: string, 
    action: 'block' | 'unblock'
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('toggle_user_block', {
        blocker_id_param: user.id,
        blocked_id_param: userId,
        action_param: action
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling user block:', error);
      return { success: false, error: `Failed to ${action} user` };
    }
  }

  // Get user connections (friends, pending requests, etc.)
  async getUserConnections(status?: 'pending' | 'accepted' | 'declined'): Promise<UserConnection[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('user_connections')
        .select(`
          *,
          requester:profiles!user_connections_requester_id_fkey(username, display_name, avatar_url),
          addressee:profiles!user_connections_addressee_id_fkey(username, display_name, avatar_url)
        `)
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user connections:', error);
      return [];
    }
  }

  // Get blocked users
  async getBlockedUsers(): Promise<UserProfile[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_blocks')
        .select(`
          blocked:profiles!user_blocks_blocked_id_fkey(
            id, username, display_name, avatar_url, email, 
            phone_number, is_phone_verified, privacy_settings,
            last_active_at, is_online, created_at, updated_at
          )
        `)
        .eq('blocker_id', user.id);

      if (error) throw error;
      return data?.map(item => item.blocked).filter(Boolean) || [];
    } catch (error) {
      console.error('Error getting blocked users:', error);
      return [];
    }
  }

  // Update user activity status
  async updateActivity(isOnline: boolean = true): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.rpc('update_user_activity', {
        user_id_param: user.id,
        is_online_param: isOnline
      });
    } catch (error) {
      console.error('Error updating user activity:', error);
    }
  }

  // Get user profile by ID (respects privacy settings)
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      
      // Check if current user can view this profile
      const canView = await this.canViewProfile(userId);
      if (!canView) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  // Check if current user can view another user's profile
  private async canViewProfile(targetUserId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // User can always view their own profile
      if (user.id === targetUserId) return true;

      // Check if target user has blocked current user
      const { data: blockData } = await supabase
        .from('user_blocks')
        .select('id')
        .eq('blocker_id', targetUserId)
        .eq('blocked_id', user.id)
        .single();

      if (blockData) return false;

      // Get target user's privacy settings
      const { data: profileData } = await supabase
        .from('profiles')
        .select('privacy_settings')
        .eq('id', targetUserId)
        .single();

      if (!profileData) return false;

      const privacy = profileData.privacy_settings as PrivacySettings;
      
      // If profile is public, anyone can view
      if (privacy.profile_visibility === 'public') return true;

      // If profile is private, only friends can view
      if (privacy.profile_visibility === 'private' || privacy.profile_visibility === 'friends_only') {
        const { data: connectionData } = await supabase
          .from('user_connections')
          .select('status')
          .or(`and(requester_id.eq.${user.id},addressee_id.eq.${targetUserId}),and(requester_id.eq.${targetUserId},addressee_id.eq.${user.id})`)
          .eq('status', 'accepted')
          .single();

        return !!connectionData;
      }

      return false;
    } catch (error) {
      console.error('Error checking profile visibility:', error);
      return false;
    }
  }

  // Get friend suggestions based on mutual connections
  async getFriendSuggestions(limit: number = 10): Promise<UserSearchResult[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get users who are friends with current user's friends but not with current user
      const { data, error } = await supabase.rpc('search_users', {
        searcher_id_param: user.id,
        search_query_param: '',
        search_type_param: 'all',
        limit_param: limit
      });

      if (error) throw error;
      
      // Filter to only show users with mutual connections and no existing connection
      return (data || []).filter((result: UserSearchResult) => 
        result.mutual_connections_count > 0 && 
        result.connection_status === 'none'
      );
    } catch (error) {
      console.error('Error getting friend suggestions:', error);
      return [];
    }
  }
}

export default new PrivacyService(); 